import { Order, CreateOrderRequest } from '../entities/Order';
import { IOrderRepository } from '../repositories/IOrderRepository';
import { User } from '../entities/User';

export class OrderUseCases {
  constructor(private orderRepository: IOrderRepository) {}

  async getOrdersForUser(user: User): Promise<Order[]> {
    // Business logic: Different roles see different orders
    switch (user.role) {
      case 'salesperson':
        return this.orderRepository.getByUserRole(user.id, 'salesperson');
      case 'manager':
        return this.orderRepository.getAll(); // Managers see all orders
      case 'warehouse':
        // Warehouse managers see only approved orders (preparing orders are handled separately)
        const warehouseOrders = await this.orderRepository.getAll();
        return warehouseOrders.filter(order => 
          order.status === 'approved'
        );
      case 'driver':
        return this.orderRepository.getByStatus('ready_for_shipping'); // Only ready orders for drivers
      case 'admin':
        return this.orderRepository.getAll(); // Admin sees everything
      default:
        return [];
    }
  }

  async approveOrder(orderId: string, user: User): Promise<Order> {
    console.log('OrderUseCases.approveOrder called with:', { orderId, userRole: user.role });
    
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    
    if (!user) {
      throw new Error('User is required');
    }
    
    if (user.role !== 'manager' && user.role !== 'admin') {
      throw new Error('Unauthorized: Only managers and admins can approve orders');
    }
    
    console.log('Fetching order from repository...');
    const order = await this.orderRepository.getById(orderId);
    console.log('Retrieved order:', order);
    console.log('Order type:', typeof order);
    console.log('Order status:', order?.status);
    console.log('Order is array?', Array.isArray(order));
    
    if (!order) {
      throw new Error('Order not found');
    }
    
    // Check if order has a valid status
    if (!order.status) {
      console.log('Order has no status, setting default status...');
      // If order has no status, assume it's pending approval
      order.status = 'pending_approval';
    }
    
    if (order.status !== 'pending_approval') {
      console.log('Order status check failed. Expected: pending_approval, Got:', order.status);
      throw new Error(`Order is not pending approval. Current status: ${order.status}`);
    }
    
    console.log('Approving order...');
    const updatedOrder = await this.orderRepository.updateStatus(orderId, 'approved');
    console.log('Order approved:', updatedOrder);
    
    return updatedOrder;
  }

  async prepareOrder(orderId: string, user: User): Promise<Order> {
    if (user.role !== 'warehouse' && user.role !== 'admin') {
      throw new Error('Unauthorized: Only warehouse managers and admins can prepare orders');
    }
    
    const order = await this.orderRepository.getById(orderId);
    if (!order) {
      throw new Error('Order not found');
    }
    
    if (order.status !== 'approved') {
      throw new Error('Order must be approved before preparation');
    }
    
    return this.orderRepository.updateStatus(orderId, 'preparing');
  }

  async shipOrder(orderId: string, user: User): Promise<Order> {
    if (user.role !== 'warehouse' && user.role !== 'admin') {
      throw new Error('Unauthorized: Only warehouse managers and admins can ship orders');
    }
    
    const order = await this.orderRepository.getById(orderId);
    if (!order) {
      throw new Error('Order not found');
    }
    
    if (order.status !== 'preparing') {
      throw new Error('Order must be preparing before shipping');
    }
    
    return this.orderRepository.updateStatus(orderId, 'ready_for_shipping');
  }

  async deliverOrder(orderId: string, user: User): Promise<Order> {
    if (user.role !== 'driver' && user.role !== 'admin') {
      throw new Error('Unauthorized: Only drivers and admins can deliver orders');
    }
    
    const order = await this.orderRepository.getById(orderId);
    if (!order) {
      throw new Error('Order not found');
    }
    
    if (order.status !== 'in_transit') {
      throw new Error('Order must be in transit before delivery');
    }
    
    return this.orderRepository.updateStatus(orderId, 'delivered');
  }

  async cancelOrder(orderId: string, user: User): Promise<Order> {
    console.log('OrderUseCases.cancelOrder called with:', { orderId, userRole: user.role });
    
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    
    if (!user) {
      throw new Error('User is required');
    }
    
    if (user.role !== 'manager' && user.role !== 'admin') {
      throw new Error('Unauthorized: Only managers and admins can cancel orders');
    }
    
    console.log('Fetching order from repository...');
    const order = await this.orderRepository.getById(orderId);
    console.log('Retrieved order for cancellation:', order);
    
    if (!order) {
      throw new Error('Order not found');
    }
    
    // Check if order can be cancelled (not already cancelled or delivered)
    if (order.status === 'cancelled') {
      throw new Error('Order is already cancelled');
    }
    
    if (order.status === 'delivered') {
      throw new Error('Cannot cancel delivered order');
    }
    
    console.log('Cancelling order...');
    const updatedOrder = await this.orderRepository.updateStatus(orderId, 'cancelled');
    console.log('Order cancelled:', updatedOrder);
    
    return updatedOrder;
  }

  async createOrder(orderData: CreateOrderRequest, user: User): Promise<Order> {
    if (user.role !== 'salesperson') {
      throw new Error('Unauthorized: Only sales representatives can create orders');
    }
    
    return this.orderRepository.create(orderData);
  }
} 